import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const headingStyle = { fontFamily: "'Sustainability', sans-serif" };
const textStyle = { fontFamily: "Sustainability, sans-serif", fontWeight: 300 };
const API_URL = "https://admin.bodiosyakdown.com/api/products";
const API_BASE = "https://admin.bodiosyakdown.com";

function normalizeCategory(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeImageValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object") {
    return value.url || value.image || value.path || value.src || value.location || "";
  }
  return "";
}

function toAbsoluteImageUrl(value) {
  const url = normalizeImageValue(value);
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/uploads/")) return `${API_BASE}${url}`;
  if (url.startsWith("uploads/")) return `${API_BASE}/${url}`;
  if (url.startsWith("/")) return `${API_BASE}${url}`;
  return `${API_BASE}/uploads/${url}`;
}

function parseJsonArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  const trimmed = value.trim();
  if (!trimmed.startsWith("[")) return [];
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function getProductImages(product) {
  const rawImages = [];
  if (product?.mainImage) rawImages.push(product.mainImage);
  if (product?.main_image) rawImages.push(product.main_image);
  if (product?.image) rawImages.push(product.image);
  if (product?.imageUrl) rawImages.push(product.imageUrl);
  rawImages.push(...parseJsonArray(product?.images));
  rawImages.push(...parseJsonArray(product?.gallery));
  parseJsonArray(product?.channel_price ?? product?.channelPrice).forEach((row) => {
    if (row?.image) rawImages.push(row.image);
  });
  return [...new Set(rawImages.map(toAbsoluteImageUrl).filter(Boolean))];
}

function getProductTimestamp(product) {
  const dateValue =
    product?.createdAt || product?.created_at ||
    product?.updatedAt || product?.updated_at ||
    product?.date || null;
  const time = dateValue ? new Date(dateValue).getTime() : 0;
  if (Number.isFinite(time) && time > 0) return time;
  return Number(product?.id || 0);
}

function pickLatestProduct(products) {
  if (!Array.isArray(products) || products.length === 0) return null;
  const withImages = products.filter((product) => getProductImages(product).length > 0);
  const pool = withImages.length > 0 ? withImages : products;
  return [...pool].sort((a, b) => getProductTimestamp(b) - getProductTimestamp(a))[0];
}

function getChannelFlags(product) {
  const raw = product?.channel_price ?? product?.channelPrice;
  const channelPrice = Array.isArray(raw)
    ? raw
    : (() => {
        try { return JSON.parse(raw || "[]"); } catch { return []; }
      })();
  return channelPrice[0] || {};
}

function isTruthyFlag(value) {
  return value === true || value === 1 || value === "1";
}

function hasLookFlag(product, key) {
  return isTruthyFlag(getChannelFlags(product)[key]);
}

function pickFeaturedLookImage(products, flag, category) {
  const featured = products.find(
    (product) => hasLookFlag(product, flag) && getProductImages(product).length > 0
  );
  if (featured) return featured;
  return pickLatestProduct(
    products.filter((product) => normalizeCategory(product?.category) === category)
  );
}

function toLookItem(product, idx) {
  const images = getProductImages(product);
  return {
    id: product?.id ?? `look-${idx}`,
    name: product?.name || `Look ${idx + 1}`,
    material: product?.material || product?.fabric || product?.category || "Look",
    image: images[0] || "/placeholder.png",
    href: product?.id ? `/product/${product.id}` : "/category/accessories",
  };
}

const fallbackLookItems = [
  {
    id: 1,
    name: "Franco Shirt",
    material: "Linen",
    image:
      "https://images.unsplash.com/photo-1596755094514-f87e34085b2c?auto=format&fit=crop&w=700&q=80",
    href: "/category/men",
  },
  {
    id: 2,
    name: "Riviera Jacket",
    material: "Cotton Blend",
    image:
      "https://images.unsplash.com/photo-1507680434567-5739c80be1ac?auto=format&fit=crop&w=700&q=80",
    href: "/category/men",
  },
  {
    id: 3,
    name: "Milano Trousers",
    material: "Wool Silk",
    image:
      "https://images.unsplash.com/photo-1473966968600-fa801b869a1a?auto=format&fit=crop&w=700&q=80",
    href: "/category/men",
  },
  {
    id: 4,
    name: "Siena Knit",
    material: "Cashmere",
    image:
      "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=700&q=80",
    href: "/category/men",
  },
];

export default function LooksEditorialSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetchProducts = async () => {
      try {
        const res = await axios.get(API_URL);
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
        if (!cancelled) setProducts(list);
      } catch (error) {
        console.error("Failed to fetch editorial section products:", error);
        if (!cancelled) setProducts([]);
      }
    };
    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, []);

  const categoryImages = useMemo(() => {
    const toImage = (product) => {
      const images = product ? getProductImages(product) : [];
      return images[0] || "/placeholder.png";
    };

    return {
      men: toImage(pickFeaturedLookImage(products, "featuredLookMen", "men")),
      women: toImage(pickFeaturedLookImage(products, "featuredLookWomen", "women")),
      accessories: toImage(pickFeaturedLookImage(products, "featuredLookAccessories", "accessories")),
    };
  }, [products]);

  const accessoryLooks = useMemo(() => {
    const featuredLooks = products
      .filter((product) => hasLookFlag(product, "featuredLook") && getProductImages(product).length > 0)
      .sort((a, b) => {
        const orderA = Number(getChannelFlags(a).featuredLookOrder) || 0;
        const orderB = Number(getChannelFlags(b).featuredLookOrder) || 0;
        return orderA - orderB || getProductTimestamp(b) - getProductTimestamp(a);
      })
      .slice(0, 4)
      .map(toLookItem);

    if (featuredLooks.length > 0) return featuredLooks;

    const latestAccessories = products
      .filter(
        (product) =>
          normalizeCategory(product?.category) === "accessories" &&
          getProductImages(product).length > 0
      )
      .sort((a, b) => getProductTimestamp(b) - getProductTimestamp(a))
      .slice(0, 4)
      .map(toLookItem);

    return latestAccessories.length > 0 ? latestAccessories : fallbackLookItems;
  }, [products]);

  const total = accessoryLooks.length;
  const activeLook = accessoryLooks[activeIndex] || accessoryLooks[0];

  useEffect(() => {
    if (activeIndex >= total) setActiveIndex(0);
  }, [activeIndex, total]);

  const next = () => {
    if (total <= 1) return;
    setActiveIndex((prev) => (prev + 1) % total);
  };
  const prev = () => {
    if (total <= 1) return;
    setActiveIndex((prev) => (prev - 1 + total) % total);
  };

  return (
    <section className="w-full mt-8 md:mt-12">
      <div className="mx-auto max-w-[1500px]">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Top left: hero model image */}
          <div className="hidden md:block min-h-[420px] md:min-h-[640px]">
            <img
              src={categoryImages.men}
              alt="Editorial menswear look"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>

          {/* Top right: look slider panel */}
          <div className="relative flex flex-col items-center justify-center px-6 md:px-10 py-10 md:py-12">
            <h3 className="text-[34px] md:text-[42px] text-[#2a241e] mb-7" style={headingStyle}>
              The Look
            </h3>

            <div className="relative w-full max-w-[260px] mx-auto">
              <button
                onClick={prev}
                type="button"
                aria-label="Previous look"
                className="absolute -left-10 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full border border-[#d7d0c4] bg-white/80 text-[#8f8474] hover:bg-white transition-colors"
              >
                ‹
              </button>
              <button
                onClick={next}
                type="button"
                aria-label="Next look"
                className="absolute -right-10 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full border border-[#d7d0c4] bg-white/80 text-[#8f8474] hover:bg-white transition-colors"
              >
                ›
              </button>

              <Link to={activeLook.href} className="block bg-[#f1eee7] p-4">
                <img
                  src={activeLook.image}
                  alt={activeLook.name}
                  className="w-full aspect-[4/5] object-cover"
                  loading="lazy"
                />
              </Link>
            </div>

            <div className="mt-6 text-center">
              <p className="text-[20px] text-[#2c2520]" style={headingStyle}>
                {activeLook.name}
              </p>
              <p className="text-[18px] text-[#6e6457] mt-1" style={textStyle}>
                {activeLook.material}
              </p>
            </div>

            <div className="mt-5 text-[18px] text-[#7f7569]" style={textStyle}>
              {activeIndex + 1} / {total}
            </div>

            <Link
              to="/category/accessories"
              className="mt-7 inline-flex items-center justify-center border border-[#d6cdbf] px-6 py-2 text-[14px] text-[#5c5347] hover:bg-[#ece7de] transition-colors"
              style={{ ...textStyle, fontWeight: 400 }}
            >
              View all looks
            </Link>
          </div>

          {/* Bottom left: story card */}
          <div className="px-6 md:px-10 py-9 md:py-12 flex flex-col items-center justify-center text-center h-full">
            <img
              src={categoryImages.women}
              alt="Delicate finishing details"
              className="w-full max-w-[290px] aspect-[5/6] object-cover"
              loading="lazy"
            />
            <h4 className="mt-6 text-[31px] text-[#2c2520]" style={headingStyle}>
              Delicate finishing accents
            </h4>
            <p className="mt-3 text-[14px] lg:text-[18px] leading-7 text-[#6a6053] max-w-[390px]" style={textStyle}>
              Spun from precious silks and cashmeres, Bodios scarves combine a
              sensorial touch with a fluid drape.
            </p>
            <div className="mt-7 space-y-3 text-[18px]">
              <Link to="/category/women" className="block text-[#8b4d2f] hover:opacity-75" style={headingStyle}>
                – Women –
              </Link>
              <Link to="/category/men" className="block text-[#8b4d2f] hover:opacity-75" style={headingStyle}>
                – Men –
              </Link>
            </div>
          </div>

          {/* Bottom right: large campaign image */}
          <div className="hidden md:block bg-[#ece8df]">
            <img
              src={categoryImages.accessories}
              alt="Signature textile chair drape"
              className="h-full w-full object-cover min-h-[420px] md:min-h-[560px]"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
